import { ISubscriptionInDTO } from '../../repository/jago/model/input/ISubscriptionInDTO';
import * as CreditsAPI from '../../repository/jago/creditsAPI';
import { IPricelistApplyApi } from './types';

const PAGE_SIZE = 100;

const MAIN_TIER_FETCH_CONCURRENCY = 6;

/**
 * Fetches every subscription of a marketplace (main tiers and plugins).
 * The backend returns only subscriptions with a null mainTier when the `mainTier` filter is omitted
 * (SubscriptionJpaRepository.getSubscriptions), so tiered plugins must be fetched per main tier:
 * without them the diff marks existing plugins as NEW and the import POSTs duplicates.
 */
export async function fetchAllMarketplaceSubscriptions(marketplaceId: number): Promise<ISubscriptionInDTO[]> {
  const untiered = await fetchAllPages(marketplaceId);
  const mainTierIds = untiered.filter(s => s.resource === 'DOMAIN').map(s => s.id);
  const byId = new Map<number, ISubscriptionInDTO>(untiered.map((s): [number, ISubscriptionInDTO] => [s.id, s]));
  for (let i = 0; i < mainTierIds.length; i += MAIN_TIER_FETCH_CONCURRENCY) {
    const batch = mainTierIds.slice(i, i + MAIN_TIER_FETCH_CONCURRENCY);
    const results = await Promise.all(batch.map(mainTier => fetchAllPages(marketplaceId, mainTier)));
    results.forEach(plugins => plugins.forEach(p => byId.set(p.id, p)));
  }
  return Array.from(byId.values());
}

async function fetchAllPages(marketplaceId: number, mainTier?: number): Promise<ISubscriptionInDTO[]> {
  const all: ISubscriptionInDTO[] = [];
  let page = 0;
  let isLast = false;
  while (!isLast) {
    const response = await CreditsAPI.fetchMarketplaceSubscriptions(marketplaceId, { page, pageSize: PAGE_SIZE, mainTier });
    const data = response && response.data;
    if (!data || !Array.isArray(data.content)) {
      throw new Error('Unexpected subscriptions response');
    }
    all.push(...data.content);
    isLast = data.last === true || data.content.length === 0 || (data.totalPages !== undefined && page + 1 >= data.totalPages);
    page += 1;
  }
  return all;
}

export const pricelistApplyApi: IPricelistApplyApi = {
  createSubscription: async (marketplaceId, data) => (await CreditsAPI.createMarketplaceSubscription(marketplaceId, data)).data,
  editSubscription: async (marketplaceId, subscriptionId, data) => (await CreditsAPI.editMarketplaceSubscription(marketplaceId, subscriptionId, data)).data,
};
